import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Divider,
  Tooltip,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { API_BASE } from '../../utils/api';
import '../../styles/CoverCard.css';

const resolveCoverSrc = (val) => {
  const v = (val || '').trim();
  if (!v) return null;
  if (v.startsWith('http://') || v.startsWith('https://')) return v;
  if (v.startsWith('/static/')) return `${API_BASE}${v}`;
  if (v.startsWith('/')) return `${API_BASE}${v}`;
  // S3 keys (e.g. "covers/id/file.jpg") contain '/' and cannot be used as static URLs
  if (v.includes('/')) return null;
  // Legacy filename stored in static directory
  return `${API_BASE}/static/images/book-covers/${v}`;
};

const statusLabels = {
  in_progress: 'In Progress',
  waiting_for_approval: 'Waiting Approval',
  approved: 'Approved',
  in_review: 'In Review',
  rejected: 'Rejected',
  reviewer_approved: 'Reviewer Approved',
  assigned: 'Assigned',
};

const statusColors = {
  in_progress: '#D4AF37',
  waiting_for_approval: '#FF9800',
  approved: '#4CAF50',
  in_review: '#2196F3',
  rejected: '#f44336',
  reviewer_approved: '#4CAF50',
  assigned: '#8BC34A',
};

const CoverCard = ({ cover, onClick, onDelete, onLinkManuscript, onUnlinkManuscript, onOpenStructure, versionName }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const hasMenu = onDelete || onLinkManuscript || onUnlinkManuscript || onOpenStructure;

  const handleMenuOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = (e) => {
    if (e) e.stopPropagation();
    setAnchorEl(null);
  };

  const handleOpenStructure = (e) => {
    e.stopPropagation();
    onOpenStructure?.(cover.id);
    handleMenuClose(e);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(cover.id);
    handleMenuClose(e);
  };

  const handleLinkManuscript = (e) => {
    e.stopPropagation();
    onLinkManuscript?.(cover.id);
    handleMenuClose(e);
  };

  const handleUnlinkManuscript = (e) => {
    e.stopPropagation();
    onUnlinkManuscript?.(cover.id);
    handleMenuClose(e);
  };

  // Get cover status from the first version if available
  const versions = cover.coverVersions || [];
  const activeVersion = versions.find(v => v.id === cover.activeCoverVersionId) || versions[0];
  const coverStatus = activeVersion?.coverStatus || 'in_progress';
  const coverType = activeVersion?.coverType;
  const previewImage = resolveCoverSrc(cover.cover_url || cover.coverImage);

  return (
    <Paper
      className="cover-card"
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Cover preview area */}
      <Box
        className="cover-card-preview"
        sx={{
          width: '100%',
          height: 200,
          backgroundColor: '#413B8D',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {previewImage ? (
          <img
            src={previewImage}
            alt={cover.title}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
          />
        ) : (
          <Typography
            variant="body2"
            sx={{ color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}
          >
            No design yet
          </Typography>
        )}

        {/* Menu button */}
        {hasMenu && (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.3)',
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.5)' },
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              onClick={(e) => e.stopPropagation()}
            >
              {onOpenStructure && (
                <MenuItem onClick={handleOpenStructure}>
                  <AutoStoriesIcon fontSize="small" sx={{ mr: 1 }} />
                  Book Cover Structure Template
                </MenuItem>
              )}
              {onOpenStructure && (onLinkManuscript || onUnlinkManuscript || onDelete) && <Divider />}
              {onLinkManuscript && (
                <MenuItem onClick={handleLinkManuscript}>
                  <LinkIcon fontSize="small" sx={{ mr: 1 }} />
                  {cover.linkedManuscriptTitle ? 'Change Manuscript Link' : 'Link to Manuscript'}
                </MenuItem>
              )}
              {onUnlinkManuscript && cover.linkedManuscriptTitle && (
                <MenuItem onClick={handleUnlinkManuscript}>
                  <LinkOffIcon fontSize="small" sx={{ mr: 1 }} />
                  Unlink Manuscript
                </MenuItem>
              )}
              {(onLinkManuscript || onUnlinkManuscript) && onDelete && <Divider />}
              {onDelete && <MenuItem onClick={handleDelete}>Delete</MenuItem>}
            </Menu>
          </>
        )}
      </Box>

      {/* Card info */}
      <Box sx={{ p: 1.5 }}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {cover.title || 'Untitled Cover'}
        </Typography>

        {cover.genre && (
          <Typography variant="caption" color="text.secondary">
            Genre: {cover.genre}
          </Typography>
        )}

        {cover.volume && (
          <Typography variant="caption" color="text.secondary" display="block">
            Volume: {cover.volume}
          </Typography>
        )}

        {/* Linked manuscript badge */}
        {cover.linkedManuscriptTitle && (
          <Tooltip title={`Manuscript: ${cover.linkedManuscriptTitle}`} placement="top">
            <Chip
              icon={<LinkIcon sx={{ fontSize: '0.75rem !important' }} />}
              label={cover.linkedManuscriptTitle}
              size="small"
              sx={{
                mt: 0.5,
                maxWidth: '100%',
                fontSize: '0.65rem',
                height: 20,
                bgcolor: '#ede9fe',
                color: '#5b21b6',
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
          </Tooltip>
        )}

        {/* Status badge */}
        <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip
            label={statusLabels[coverStatus] || 'In Progress'}
            size="small"
            sx={{
              backgroundColor: statusColors[coverStatus] || '#D4AF37',
              color: '#fff',
              fontSize: '0.7rem',
              height: 22,
            }}
          />
          {coverType && (
            <Chip
              label={coverType}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          )}
          {versionName && (
            <Chip
              label={versionName}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 22 }}
            />
          )}
        </Box>

        {/* Design Cover button */}
        <Button
          size="small"
          variant="contained"
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          sx={{
            mt: 1,
            backgroundColor: '#D4AF37',
            color: '#1A1A1A',
            textTransform: 'none',
            fontSize: '0.8rem',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#F5D76E' },
          }}
        >
          Design Cover
        </Button>
      </Box>
    </Paper>
  );
};

export default CoverCard;
